// Apresentação de status do softphone — puro (sem React/DOM), para a UI e os testes.
import type { SoftphoneStatus } from "./use-softphone.js";
import i18n from "../i18n.js";

type BadgeVariant = "default" | "secondary" | "destructive" | "outline";

export interface StatusMeta {
  /** Rótulo em português para o badge de status. */
  label: string;
  /** Variante do <Badge> shadcn. */
  variant: BadgeVariant;
  /** True enquanto há uma transição em andamento (mostra spinner). */
  busy: boolean;
}

const META: Record<SoftphoneStatus, Omit<StatusMeta, "label"> & { key: string }> = {
  loading: { key: "calling.statuses.preparing", variant: "secondary", busy: true },
  unprovisioned: { key: "calling.statuses.unavailable", variant: "outline", busy: false },
  disconnected: { key: "calling.statuses.disconnected", variant: "outline", busy: false },
  connecting: { key: "calling.statuses.connecting", variant: "secondary", busy: true },
  registered: { key: "calling.statuses.ready", variant: "default", busy: false },
  calling: { key: "calling.statuses.calling", variant: "secondary", busy: true },
  "in-call": { key: "calling.statuses.inCall", variant: "default", busy: false },
  ending: { key: "calling.statuses.ending", variant: "secondary", busy: true },
  error: { key: "calling.statuses.error", variant: "destructive", busy: false },
};

export function statusMeta(status: SoftphoneStatus): StatusMeta {
  const { key, ...meta } = META[status];
  return { ...meta, label: i18n.t(key) };
}

// Linha de tempo do visor da chamada. Separa explicitamente os dois tempos:
// enquanto CHAMA, o que corre é a espera ("Chamando… 0:12"); depois que
// atendem, o cronômetro grande é o tempo FALADO e a espera vira o registro
// parado do quanto se esperou por aquela ligação. Fora de chamada, só o rótulo
// do status. Puro para poder ser testado sem React.
export function liveTimeLabel(
  status: SoftphoneStatus,
  callSeconds: number,
  waitSeconds: number,
): string {
  if (status === "in-call") {
    const talked = formatDuration(callSeconds);
    return waitSeconds > 0
      ? `${talked} · ${i18n.t("calling.statuses.waited", { duration: formatDuration(waitSeconds) })}`
      : talked;
  }
  if (status === "calling") return `${statusMeta("calling").label} ${formatDuration(waitSeconds)}`;
  return statusMeta(status).label;
}

/** Erro técnico gerado ao cancelar uma tentativa de conexão já substituída. */
export function isConnectAbortedError(message: string | null | undefined): boolean {
  return typeof message === "string" && /connect aborted/i.test(message);
}

// Formata segundos como mm:ss (ou hh:mm:ss acima de 1h).
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}
